import { ExternalLink as ExternalLinkIcon, GitPullRequest } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface SessionPrInfoProps {
  /** PR URL to display */
  prUrl?: string
  /** Additional CSS classes */
  className?: string
}

/**
 * Extract PR number from Azure DevOps PR URL.
 * Supports both dev.azure.com and visualstudio.com formats.
 */
function extractPrNumber(url: string): string | null {
  // Match patterns like /pullrequest/1234
  const match = url.match(/\/pullrequest\/(\d+)/i)
  return match ? match[1] : null
}

/**
 * Displays session pull request information with external link.
 * Returns null if prUrl is not provided.
 *
 * @example
 * ```tsx
 * <SessionPrInfo prUrl="https://dev.azure.com/org/project/_git/repo/pullrequest/1234" />
 * ```
 */
export function SessionPrInfo({ prUrl, className }: SessionPrInfoProps) {
  if (!prUrl) {
    return null
  }

  const prNumber = extractPrNumber(prUrl)
  const displayText = prNumber ? `PR #${prNumber}` : 'View PR'

  return (
    <div className={cn('space-y-1', className)}>
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <GitPullRequest className="h-4 w-4" data-testid="pr-icon" />
        <span>Pull Request</span>
      </div>
      <a
        href={prUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
      >
        {displayText}
        <ExternalLinkIcon className="h-3 w-3" data-testid="external-link-icon" />
      </a>
    </div>
  )
}
